import { useRef, useCallback, useState } from "react";
import { useTranslations, useErrorContext } from "tds_shared_ui";
import { buildPlaceOrderRequest } from "../utils/buildPlaceOrderRequest";

function isStoredCardTokenized(data) {
  return Boolean(data?.payment?.id);
}

function hasCardDisplayFields(walletResult) {
  return Boolean(
    (walletResult?.cardBrand ||
      walletResult?.brand ||
      walletResult?.payment?.brand) &&
      (walletResult?.cardLast4Digits ||
        walletResult?.lastFour ||
        walletResult?.payment?.lastFour)
  );
}

/**
 * Merges getPaymentMethods stored methods onto the tokenized wallet result
 * so buildPaymentFields can map brand/lastFour → cardBrand/cardLast4Digits.
 */
async function enrichWalletResultWithPaymentMethods(
  walletResult,
  getPaymentMethods
) {
  if (!walletResult || hasCardDisplayFields(walletResult)) {
    return walletResult;
  }
  if (typeof getPaymentMethods !== "function") {
    return walletResult;
  }

  try {
    const paymentMethods = await getPaymentMethods();
    const storedPaymentMethods = paymentMethods?.storedPaymentMethods;
    if (!Array.isArray(storedPaymentMethods) || !storedPaymentMethods.length) {
      return walletResult;
    }
    return { ...walletResult, storedPaymentMethods };
  } catch {
    return walletResult;
  }
}

export function usePlaceOrder({
  onPlaceOrder,
  onSubmit,
  checkoutSummary,
  formValues,
}) {
  const { translate } = useTranslations(["common"]);
  const { clearError, displayError } = useErrorContext();
  const [loading, setLoading] = useState(false);
  const [paymentFormValid, setPaymentFormValid] = useState(null);
  const [submitPaymentReady, setSubmitPaymentReady] = useState(false);

  const submitPaymentFormRef = useRef(null);
  const getPaymentMethodsRef = useRef(null);
  const pendingExtraFieldsRef = useRef(null);
  const walletResultRef = useRef(null);

  const ccRequired = checkoutSummary?.ccRequired;
  const paymentBlocked =
    ccRequired && (!submitPaymentReady || paymentFormValid === false);

  const handleValidationChange = useCallback((isValid) => {
    setPaymentFormValid(isValid);
  }, []);

  const handleSubmitAvailable = useCallback((submitOrApi) => {
    // Supports either submitPaymentForm(fn) or
    // { submitPaymentForm, getPaymentMethods } from tds_shared_ui.
    if (typeof submitOrApi === "function") {
      submitPaymentFormRef.current = submitOrApi;
      getPaymentMethodsRef.current = submitOrApi.getPaymentMethods || null;
    } else {
      submitPaymentFormRef.current = submitOrApi?.submitPaymentForm || null;
      getPaymentMethodsRef.current = submitOrApi?.getPaymentMethods || null;
    }
    setSubmitPaymentReady(Boolean(submitPaymentFormRef.current));
  }, []);

  const submitOrder = useCallback(
    async (extraFields, walletResult) => {
      setLoading(true);
      clearError();

      const enrichedWalletResult = await enrichWalletResultWithPaymentMethods(
        walletResult,
        getPaymentMethodsRef.current
      );

      const request = buildPlaceOrderRequest({
        walletResult: enrichedWalletResult,
        formValues,
        extraFields,
      });

      if (ccRequired && !request.storedPaymentMethodId) {
        displayError(translate("ERROR_GENERIC"));
        setLoading(false);
        return Promise.reject(new Error("Missing stored payment method"));
      }

      return onPlaceOrder(request)
        .then((result) => {
          walletResultRef.current = null;
          return onSubmit(result);
        })
        .catch((error) => {
          //walletResultRef.current = null;
          displayError(
            error.translatedMessage || translate("ERROR_GENERIC")
          );
          throw error;
        })
        .finally(() => setLoading(false));
    },
    [
      onPlaceOrder,
      onSubmit,
      ccRequired,
      formValues,
      translate,
      clearError,
      displayError,
    ]
  );

  const handlePaymentSuccess = useCallback((data) => {
    if (!isStoredCardTokenized(data)) {
      if (pendingExtraFieldsRef.current === null) {
        setLoading(false);
      }
      return;
    }

    // Cache token only; placeOrder awaits submitOrder after payment submit.
    walletResultRef.current = data;
  }, []);

  const handlePaymentError = useCallback(() => {
    pendingExtraFieldsRef.current = null;
    setLoading(false);
  }, []);

  const placeOrder = useCallback(
    async (extraFields = {}) => {
      if (!ccRequired) {
        return submitOrder(extraFields, null);
      }

      if (!submitPaymentFormRef.current && !walletResultRef.current) {
        displayError(translate("ERROR_GENERIC"));
        throw new Error("Payment form unavailable");
      }

      pendingExtraFieldsRef.current = extraFields;
      setLoading(true);
      clearError();

      if (walletResultRef.current) {
        pendingExtraFieldsRef.current = null;
        return submitOrder(extraFields, walletResultRef.current);
      }

      try {
        await submitPaymentFormRef.current();
      } catch (error) {
        if (walletResultRef.current) {
          pendingExtraFieldsRef.current = null;
          return submitOrder(extraFields, walletResultRef.current);
        }

        console.error("Payment form submit failed", {
          error,
          message: error?.message,
          name: error?.name,
          stack: error?.stack,
          stringified: (() => {
            try {
              return JSON.stringify(error);
            } catch {
              return String(error);
            }
          })(),
        });

        pendingExtraFieldsRef.current = null;
        setLoading(false);
        displayError(translate("ERROR_GENERIC"));
        throw error instanceof Error
          ? error
          : new Error("Payment submission failed");
      }

      if (walletResultRef.current) {
        const pending = pendingExtraFieldsRef.current ?? extraFields;
        pendingExtraFieldsRef.current = null;
        return submitOrder(pending, walletResultRef.current);
      }

      pendingExtraFieldsRef.current = null;
      setLoading(false);
      displayError(translate("ERROR_GENERIC"));
      throw new Error("Missing stored payment method");
    },
    [
      ccRequired,
      submitOrder,
      clearError,
      displayError,
      translate,
    ]
  );

  return {
    loading,
    paymentBlocked,
    placeOrder,
    onPaymentSuccess: handlePaymentSuccess,
    onPaymentError: handlePaymentError,
    onValidationChange: handleValidationChange,
    onSubmitAvailable: handleSubmitAvailable,
  };
}
